/**
 * Live video adapter. The class room talks only to this small interface, so the video provider can change
 * without touching the screens:
 *
 *   const video = await createVideo(joinInfo)       // joinInfo is the `video` object from POST /classes/:id/join
 *   await video.connect(stageElement, { onChange }) // attaches remote tracks to the element
 *   await video.setMic(true) / setCamera(true) / setShare(true)
 *   video.disconnect()
 *
 * Two modes:
 *  - "livekit": the LiveKit browser SDK (loaded once from config.livekitClientUrl) connects with the
 *    short-lived token the server issued for this person.
 *  - "none": no media (development, or no SDK configured). The room still works for chat, hands and polls,
 *    and says clearly that video is off.
 *
 * Written against the LiveKit client SDK's documented API (v2). The server side (join tokens, mute, remove, webhooks) has
 * been checked against a real LiveKit server; this file itself could not be run with the real SDK where it was written
 * (the SDK could not be downloaded there). Test one live class with two real browsers before launch (see README).
 */
import { config } from '../../core/config.js';

let sdkPromise = null;

function loadSdk() {
  if (window.LivekitClient) return Promise.resolve(window.LivekitClient);
  if (!sdkPromise) {
    sdkPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = config.livekitClientUrl;
      script.async = true;
      script.onload = () => (window.LivekitClient ? resolve(window.LivekitClient) : reject(new Error('Video library did not load.')));
      script.onerror = () => {
        sdkPromise = null;
        reject(new Error('The video library could not be loaded.'));
      };
      document.head.append(script);
    });
  }
  return sdkPromise;
}

class NoVideo {
  mode = 'none';

  async connect() {}

  async setMic() {}

  async setCamera() {}

  async setShare() {}

  disconnect() {}
}

class LiveKitVideo {
  mode = 'livekit';

  constructor(sdk, info) {
    this.sdk = sdk;
    this.info = info;
    this.room = null;
    this.stage = null;
  }

  async connect(stage, { onChange } = {}) {
    const { Room, RoomEvent } = this.sdk;
    this.stage = stage;
    this.room = new Room({ adaptiveStream: true, dynacast: true });
    const refresh = () => onChange?.();
    this.room
      .on(RoomEvent.TrackSubscribed, (track, _publication, participant) => this.attach(track, participant))
      .on(RoomEvent.TrackUnsubscribed, (track) => this.detach(track))
      .on(RoomEvent.ParticipantDisconnected, (participant) => this.stage.querySelector(`[data-identity="${CSS.escape(participant.identity)}"]`)?.remove())
      .on(RoomEvent.Disconnected, refresh);
    await this.room.connect(this.info.url, this.info.token);
    // Tracks that were already published before we joined.
    this.room.remoteParticipants?.forEach((participant) => {
      participant.trackPublications.forEach((publication) => {
        if (publication.track) this.attach(publication.track, participant);
      });
    });
  }

  tileFor(participant, kind) {
    const id = `${participant.identity}:${kind}`;
    let tile = this.stage.querySelector(`[data-tile="${CSS.escape(id)}"]`);
    if (!tile) {
      tile = document.createElement('div');
      tile.className = `tile vtile ${kind === 'screen' ? 'main' : ''}`;
      tile.dataset.tile = id;
      tile.dataset.identity = participant.identity;
      const name = document.createElement('span');
      name.className = 'nm';
      name.textContent = participant.name || participant.identity;
      tile.append(name);
      this.stage.append(tile);
    }
    return tile;
  }

  attach(track, participant) {
    const { Track } = this.sdk;
    if (track.kind === Track.Kind.Audio) {
      const el = track.attach();
      el.dataset.audioFor = participant.identity;
      this.stage.append(el);
      return;
    }
    const kind = track.source === Track.Source.ScreenShare ? 'screen' : 'camera';
    const tile = this.tileFor(participant, kind);
    tile.querySelector('video')?.remove();
    const el = track.attach();
    el.setAttribute('playsinline', '');
    tile.prepend(el);
  }

  detach(track) {
    track.detach().forEach((el) => el.remove());
  }

  async setMic(on) {
    await this.room?.localParticipant.setMicrophoneEnabled(on);
  }

  async setCamera(on) {
    await this.room?.localParticipant.setCameraEnabled(on);
  }

  async setShare(on) {
    await this.room?.localParticipant.setScreenShareEnabled(on);
  }

  disconnect() {
    this.room?.disconnect();
    this.room = null;
    this.stage?.replaceChildren();
  }
}

export async function createVideo(info) {
  if (!info || info.provider === 'fake' || !config.livekitClientUrl) return new NoVideo();
  try {
    return new LiveKitVideo(await loadSdk(), info);
  } catch {
    return new NoVideo();
  }
}
